import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Plus, Pencil, Trash2, CalendarDays } from "lucide-react";

import {
  blockAvailability,
  blockRecurringAvailability,
  updateAvailability,
  updateAvailabilityGroup,
  updateRecurringAvailability,
  unblockAvailability,
  unblockAvailabilityGroup,
  deleteRecurringAvailability,
  createRecurringUnblockException,
  listAdminBookings,
} from "./availabilityApi";

import useTableController from "../../../components/table/useTableController";
import { listVenues } from "./venuesApi";
import { listVenueGrounds } from "./venueGroundsApi";
import TableToolbar from "../../../components/table/TableToolbar";
import Paginator from "../../../components/table/Paginator";
import ConfirmDialog from "../../../components/common/ConfirmDialog";
import ReusableModal from "../../../components/common/ReusableModal";
import FormButton from "../../../components/form/FormButton";

import BlockAvailabilityForm, {
  emptyBlock,
  toISODate,
  toHHMM,
  todayISO,
  isWholeDayTimes,
} from "./BlockAvailabilityForm";

import "./availability.css";
import AvailabilityBoard from "./AvailabilityBoard";

const formatDisplayDate = (value) => {
  const iso = toISODate(value);
  if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return value || "—";

  const [yyyy, mm, dd] = iso.split("-");
  return `${dd}/${mm}/${yyyy}`;
};

const formatDisplayDateLabel = (label) => {
  if (!label) return "—";

  return String(label).replace(/\d{4}-\d{2}-\d{2}/g, (match) =>
    formatDisplayDate(match),
  );
};

const getDayLabel = (value) => {
  const iso = toISODate(value);
  if (!iso) return "";

  const date = new Date(iso);
  return date.toLocaleDateString("en-US", { weekday: "short" });
};

const format12HourTime = (time) => {
  const [hh, mm = "00"] = String(time || "00:00").split(":");
  const hour24 = Number(hh);
  const hour12 = hour24 % 12 || 12;
  const ampm = hour24 >= 12 ? "PM" : "AM";

  return {
    time: `${hour12}:${String(mm).padStart(2, "0")}`,
    ampm,
  };
};

const pickBestErrorMessage = (error) => {
  const data = error?.response?.data || {};
  const message =
    data?.message || data?.error || error?.message || "Something went wrong";

  return String(message || "Something went wrong");
};

const isConflictError = (error) => {
  const status = error?.response?.status;
  const code = error?.response?.data?.code;
  const message = pickBestErrorMessage(error).toLowerCase();

  return (
    status === 409 ||
    code === "CONFLICT" ||
    message.includes("conflict") ||
    message.includes("already") ||
    message.includes("overlap") ||
    message.includes("blocked") ||
    message.includes("booking")
  );
};

const formatConflictDetails = (error) => {
  const data = error?.response?.data || {};
  const conflicts = Array.isArray(data?.conflicts) ? data.conflicts : [];
  const skipped = Array.isArray(data?.skipped) ? data.skipped : [];
  const parts = [];

  if (conflicts.length) {
    const dates = conflicts
      .map((conflict) => conflict?.date)
      .filter(Boolean)
      .slice(0, 6);

    parts.push(`Conflicts: ${dates.join(", ")}${conflicts.length > 6 ? "…" : ""}`);
  }

  if (skipped.length) {
    const dates = skipped
      .map((item) => item?.date)
      .filter(Boolean)
      .slice(0, 6);

    parts.push(
      `Skipped (existing blocks): ${dates.join(", ")}${
        skipped.length > 6 ? "…" : ""
      }`,
    );
  }

  return parts.join(" • ");
};

const GroundAvailabilityPage = () => {
  const { groundId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const isBlockedListView = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("view") === "blocked-list";
  }, [location.search]);

  const [venues, setVenues] = useState([]);

  const forcedVenueId = useMemo(() => {
    if (venues.length === 1) return String(venues[0]._id || venues[0].id || "");
    return "";
  }, [venues]);

  const [groundsByVenue, setGroundsByVenue] = useState({});

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const [exceptionRow, setExceptionRow] = useState(null);
  const [exceptionDate, setExceptionDate] = useState("");
  const [exceptionNote, setExceptionNote] = useState("");
  const [exceptionBusy, setExceptionBusy] = useState(false);

  const [modalError, setModalError] = useState("");
  const [modalErrorDetail, setModalErrorDetail] = useState("");

  const [boardRefreshKey, setBoardRefreshKey] = useState(0);
  const [boardTarget, setBoardTarget] = useState({
    venueId: "",
    groundId: "",
    date: todayISO(),
  });

  const openBlockedList = () => {
    const params = new URLSearchParams(location.search);
    params.set("view", "blocked-list");
    navigate({
      pathname: location.pathname,
      search: `?${params.toString()}`,
    });
  };

  const backToAvailability = () => {
    const params = new URLSearchParams(location.search);
    params.delete("view");
    const search = params.toString();

    navigate({
      pathname: location.pathname,
      search: search ? `?${search}` : "",
    });
  };

  const ensureGroundsForVenue = async (venueId) => {
    if (!venueId) return [];
    if (groundsByVenue[venueId]) return groundsByVenue[venueId];

    try {
      const items = await listVenueGrounds(venueId, {
        page: 1,
        pageSize: 500,
        includeInactive: true,
      });

      const list = Array.isArray(items) ? items : [];

      setGroundsByVenue((prev) => ({
        ...prev,
        [venueId]: list,
      }));

      return list;
    } catch {
      toast.error("Failed to load grounds");
      return [];
    }
  };

  const load = async (nextPage = 1, nextPageSize = 10) => {
    setLoading(true);

    const payload = {
      status: "blocked",
      page: nextPage,
      pageSize: nextPageSize,
      ...(groundId ? { groundId } : {}),
    };

    try {
      const response = await listAdminBookings(payload);

      const rawItems = Array.isArray(response)
        ? response
        : Array.isArray(response?.items)
          ? response.items
          : [];

      const normalized = rawItems.map((row) => ({
        ...row,
        date: row?.date ? toISODate(row.date) : "",
        dateLabel: row?.dateLabel || (row?.date ? toISODate(row.date) : "—"),
        startTime: toHHMM(row?.startTime),
        endTime: toHHMM(row?.endTime),
        note: row?.note || "",
        sourceType: row?.sourceType || "single",
        daysLabel: row?.daysLabel || "",
        startDate: row?.startDate || "",
        endDate: row?.endDate || "",
        days: Array.isArray(row?.days) ? row.days : [],
        blockGroupId: row?.blockGroupId || "",
        singleDayLabel: row?.date ? getDayLabel(row.date) : "",
      }));

      const computedTotal = Array.isArray(response)
        ? response.length
        : Number(response?.total) || normalized.length;

      setRows(normalized);
      setTotal(computedTotal);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to load blocked slots");
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const loadVenues = async () => {
      try {
        const response = await listVenues({ page: 1, pageSize: 200, q: "" });
        const items = Array.isArray(response?.items) ? response.items : [];

        setVenues(items);
      } catch {
        toast.error("Failed to load venues");
      }
    };

    loadVenues();
  }, []);

  useEffect(() => {
    setPage(1);
  }, [groundId]);

  useEffect(() => {
    load(page, pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groundId, page, pageSize]);

  const items = useMemo(() => {
    return (rows || []).map((row) => ({
      ...row,
      timeSpan: `${row.startTime}–${row.endTime}`,
      noteSafe: (row.note || "").trim(),
      venueName: row.venueName || "—",
      groundName: row.groundName || "—",
      venueId: row.venueId || "",
      groundId: row.groundId || "",
    }));
  }, [rows]);

  const tc = useTableController(items, {
    searchKeys: ["venueName", "groundName", "date", "timeSpan", "noteSafe"],
    initialPageSize: pageSize,
    pagination: { mode: "server", total },
  });

  const serialStart = (page - 1) * pageSize;

  const sortCaret = (key) => {
    if (tc.sortKey !== key) return null;
    return tc.sortDir === "asc" ? " ▲" : " ▼";
  };

  const setConflictModal = (error) => {
    const message = pickBestErrorMessage(error);
    const detail = formatConflictDetails(error);

    setModalError(message);
    setModalErrorDetail(detail);

    toast.error(detail ? `${message} • ${detail}` : message);
  };

  const clearModalErrors = () => {
    setModalError("");
    setModalErrorDetail("");
  };

  const handleCreate = async (payload, pickedGroundId) => {
    const targetGroundId = pickedGroundId || groundId;

    if (!targetGroundId) {
      toast.error("Select a ground.");
      throw new Error("Select a ground.");
    }

    clearModalErrors();

    try {
      const shouldUseRecurringApi =
        !!payload?.startDate &&
        !payload?.endDate &&
        Array.isArray(payload?.days) &&
        payload.days.length > 0;

      const response = shouldUseRecurringApi
        ? await blockRecurringAvailability(targetGroundId, {
            ...payload,
            isRecurring: true,
          })
        : await blockAvailability(targetGroundId, payload);

      toast.success("Blocked");
      setCreating(false);
      setPage(1);
      setBoardRefreshKey((value) => value + 1);
      await load(1, pageSize);

      return response;
    } catch (error) {
      if (isConflictError(error)) {
        setConflictModal(error);
      } else {
        toast.error(pickBestErrorMessage(error));
      }

      throw error;
    }
  };

  const handleSave = async (payload, pickedGroundId) => {
    const id = editing?._id || editing?.id;

    if (!id) {
      throw new Error("Missing id");
    }

    const rowGroundId = String(editing?.groundId || "");
    const targetGroundId = String(pickedGroundId || rowGroundId || groundId || "");

    clearModalErrors();

    try {
      if (editing?.sourceType === "recurring") {
        const recurringPayload = {
          startDate: payload.startDate,
          endDate: payload.endDate || "",
          days: Array.isArray(payload.days) ? payload.days : [],
          startTime: payload.startTime,
          endTime: payload.endTime,
          wholeDay: !!payload.wholeDay,
          note: payload.note,
        };

        await updateRecurringAvailability(id, recurringPayload);

        toast.success("Recurring block updated");
        setEditing(null);
        await load(page, pageSize);
        return;
      }

      if (editing?.sourceType === "multidate") {
        const groupId = String(editing?.blockGroupId || "");

        if (!groupId) {
          throw new Error("Missing block group id");
        }

        const patch = {
          startTime: payload.startTime,
          endTime: payload.endTime,
          note: payload.note,
        };

        await updateAvailabilityGroup(groupId, patch);

        toast.success("Multi-date block updated");
        setEditing(null);
        await load(page, pageSize);
        return;
      }

      if (targetGroundId && rowGroundId && targetGroundId === rowGroundId) {
        const patch = {
          startTime: payload.startTime,
          endTime: payload.endTime,
          note: payload.note,
        };

        await updateAvailability(id, patch);

        toast.success("Updated");
        setEditing(null);
        await load(page, pageSize);
        return;
      }

      if (!rowGroundId) {
        const patch = {
          startTime: payload.startTime,
          endTime: payload.endTime,
          note: payload.note,
        };

        await updateAvailability(id, patch);

        toast.success("Updated");
        setEditing(null);
        await load(page, pageSize);
        return;
      }

      if (!targetGroundId) {
        toast.error("Select a ground.");
        throw new Error("Select a ground.");
      }

      const createdResponse = await blockAvailability(targetGroundId, payload);

      try {
        await unblockAvailability(id);
      } catch (deleteError) {
        const createdItems = Array.isArray(createdResponse?.items)
          ? createdResponse.items
          : [];

        for (const item of createdItems) {
          const newId = item?._id || item?.id;

          if (newId) {
            try {
              // eslint-disable-next-line no-await-in-loop
              await unblockAvailability(newId);
            } catch (_) {}
          }
        }

        throw deleteError;
      }

      toast.success("Moved & Updated");
      setEditing(null);
      setPage(1);
      await load(1, pageSize);
    } catch (error) {
      if (isConflictError(error)) {
        setConflictModal(error);
      } else {
        toast.error(pickBestErrorMessage(error));
      }

      throw error;
    }
  };

  const handleUnblock = async () => {
    const row = deleting;
    const id = row?._id || row?.id;

    if (!id) return;

    setBusyId(id);

    try {
      if (row?.sourceType === "recurring") {
        await deleteRecurringAvailability(id);
        toast.success("Recurring block deleted");
      } else if (row?.sourceType === "multidate") {
        const groupId = String(row?.blockGroupId || "");

        if (!groupId) {
          throw new Error("Missing block group id");
        }

        await unblockAvailabilityGroup(groupId);
        toast.success("Multi-date block deleted");
      } else {
        await unblockAvailability(id);
        toast.success("Unblocked");
      }

      setDeleting(null);
      await load(page, pageSize);
    } catch (error) {
      toast.error(pickBestErrorMessage(error));
    } finally {
      setBusyId(null);
    }
  };

  const handleCreateException = async () => {
    const id = exceptionRow?._id || exceptionRow?.id;

    if (!id) return;

    if (!exceptionDate) {
      toast.error("Select a date.");
      return;
    }

    setExceptionBusy(true);

    try {
      await createRecurringUnblockException(id, {
        date: exceptionDate,
        note: exceptionNote.trim(),
      });

      toast.success("One-date open exception created");
      setExceptionRow(null);
      setExceptionDate("");
      setExceptionNote("");
      await load(page, pageSize);
    } catch (error) {
      toast.error(pickBestErrorMessage(error));
    } finally {
      setExceptionBusy(false);
    }
  };

  const timePill = (startTime, endTime) => {
    const whole = isWholeDayTimes(startTime, endTime);

    if (whole) {
      return <span className="gavx-time-text">Whole day</span>;
    }

    const start = format12HourTime(startTime);
    const end = format12HourTime(endTime);

    return (
      <span className="gavx-time-text">
        {start.time} <span>{start.ampm}</span>
        <em>–</em>
        {end.time} <span>{end.ampm}</span>
      </span>
    );
  };

  const ThSort = ({ keyName, children, style }) => (
    <th
      style={{ cursor: "pointer", userSelect: "none", ...(style || {}) }}
      onClick={() => tc.toggleSort(keyName)}
      title={`Sort by ${keyName}`}
    >
      {children}
      <span style={{ opacity: 0.8 }}>{sortCaret(keyName)}</span>
    </th>
  );

  return (
    <div className="sa-page gavx-page">
      {!isBlockedListView ? (
        <AvailabilityBoard
          venues={venues}
          forcedVenueId={forcedVenueId}
          ensureGroundsForVenue={ensureGroundsForVenue}
          refreshKey={boardRefreshKey}
          onOpenList={openBlockedList}
          onOpenCustom={(target) => {
            clearModalErrors();
            setBoardTarget(target);
            setCreating(true);
          }}
          onChanged={async () => {
            setBoardRefreshKey((value) => value + 1);
            await load(page, pageSize);
          }}
        />
      ) : (
        <section className="gavx-blocked-list-page" aria-labelledby="blocked-slots-title">
          <div className="gavx-blocked-page-header">
            <button
              type="button"
              className="gavx-back-button"
              onClick={backToAvailability}
              aria-label="Back to availability"
            >
              <ArrowLeft size={18} aria-hidden="true" />
              Back
            </button>

            <div className="gavx-blocked-page-heading">
              <h1 id="blocked-slots-title" className="sa-h1">Blocked Slots</h1>
              <div className="gavx-page-subtitle">
                Manage single, grouped and recurring blocks.
              </div>
            </div>

            <button
              type="button"
              className="sa-btn gavx-new-btn"
              onClick={() => {
                clearModalErrors();
                setBoardTarget((current) => ({ ...current, date: todayISO() }));
                setCreating(true);
              }}
            >
              <Plus className="lucide" /> Block slot
            </button>
          </div>

          <TableToolbar
            searchTerm={tc.searchTerm}
            onSearchChange={tc.setSearch}
            filterDefs={[]}
            filters={{}}
            onFilterChange={() => {}}
          />

          <div className="sa-card">
        {loading ? (
          <div className="sa-placeholder" style={{ height: 120 }}>
            Loading…
          </div>
        ) : tc.rows.length === 0 ? (
          <div className="sa-empty">No blocked slots found.</div>
        ) : (
          <>
            <div className="sa-table-wrap gavx-table-wrap">
              <table className="sa-table gavx-table">
                <thead>
                  <tr>
                    <th style={{ width: 60 }}>S.N.</th>
                    <ThSort keyName="venueName">Venue</ThSort>
                    <ThSort keyName="groundName">Ground</ThSort>
                    <ThSort keyName="date">Date</ThSort>
                    <th>Time</th>
                    <ThSort keyName="noteSafe">Note</ThSort>
                    <th style={{ textAlign: "right", width: 120 }}>Actions</th>
                  </tr>
                </thead>

                <tbody>
                  {tc.rows.map((row, index) => (
                    <tr key={row._id || row.id}>
                      <td>{serialStart + index + 1}</td>

                      <td className="gavx-main-text">{row.venueName || "—"}</td>

                      <td className="gavx-normal-text">{row.groundName || "—"}</td>

                      <td style={{ fontVariantNumeric: "tabular-nums" }}>
                        <div className="gavx-date-cell">
                          <CalendarDays size={13} className="gavx-date-icon" />
                          <span>
                            {formatDisplayDateLabel(row.dateLabel || row.date || "—")}
                          </span>
                        </div>

                        {(row.sourceType === "recurring" ||
                          row.sourceType === "multidate") &&
                          !!row.daysLabel && (
                            <div className="gavx-date-sub">{row.daysLabel}</div>
                          )}

                        {row.sourceType === "single" && !!row.singleDayLabel && (
                          <div className="gavx-date-sub">{row.singleDayLabel}</div>
                        )}
                      </td>

                      <td>{timePill(row.startTime, row.endTime)}</td>

                      <td
                        title={row.noteSafe || ""}
                        className={row.noteSafe ? "gavx-note-cell" : "gavx-note-cell muted"}
                      >
                        {row.noteSafe || "—"}
                      </td>

                      <td className="sa-actions-cell">
                        <div className="sa-actions">
                          <button
                            type="button"
                            className="sa-icon-link"
                            title={
                              row.sourceType === "recurring"
                                ? "Edit recurring block"
                                : row.sourceType === "multidate"
                                  ? "Edit multi-date block"
                                  : "Edit block"
                            }
                            onClick={() => {
                              clearModalErrors();

                              if (row.sourceType === "recurring") {
                                setEditing({
                                  ...row,
                                  mode: "range",
                                  date: "",
                                  startDate: row.startDate || "",
                                  endDate: row.endDate || "",
                                  startTime: row.startTime || "06:00",
                                  endTime: row.endTime || "07:00",
                                  wholeDay: isWholeDayTimes(row.startTime, row.endTime),
                                  note: row.note || "",
                                  venueId: row.venueId || "",
                                  groundId: row.groundId || "",
                                  days: Array.isArray(row.days) ? row.days : [],
                                  sourceType: "recurring",
                                });

                                return;
                              }

                              if (row.sourceType === "multidate") {
                                setEditing({
                                  ...row,
                                  mode: "range",
                                  date: "",
                                  startDate: row.startDate || "",
                                  endDate: row.endDate || "",
                                  startTime: row.startTime || "06:00",
                                  endTime: row.endTime || "07:00",
                                  wholeDay: isWholeDayTimes(row.startTime, row.endTime),
                                  note: row.note || "",
                                  venueId: row.venueId || "",
                                  groundId: row.groundId || "",
                                  days: Array.isArray(row.days) ? row.days : [],
                                  sourceType: "multidate",
                                  blockGroupId: row.blockGroupId || "",
                                  readOnlyGroupMeta: true,
                                });

                                return;
                              }

                              setEditing({
                                ...row,
                                mode: "single",
                                date: row.date || todayISO(),
                                startTime: row.startTime || "06:00",
                                endTime: row.endTime || "07:00",
                                wholeDay: isWholeDayTimes(row.startTime, row.endTime),
                                note: row.note || "",
                                venueId: row.venueId || "",
                                groundId: row.groundId || "",
                                days: [],
                                startDate: "",
                                endDate: "",
                              });
                            }}
                          >
                            <Pencil className="lucide" />
                          </button>

                          <button
                            type="button"
                            className="sa-icon-link danger"
                            title={
                              row.sourceType === "recurring"
                                ? "Delete recurring block"
                                : row.sourceType === "multidate"
                                  ? "Delete multi-date block"
                                  : "Unblock"
                            }
                            onClick={() => setDeleting(row)}
                            disabled={busyId === (row._id || row.id)}
                          >
                            <Trash2 className="lucide" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <Paginator
              page={page}
              totalPages={tc.totalPages}
              onPage={(nextPage) => setPage(nextPage)}
              pageSize={pageSize}
              onPageSizeChange={(nextPageSize) => {
                setPage(1);
                setPageSize(Number(nextPageSize));
              }}
            />
          </>
        )}
          </div>
        </section>
      )}

      {creating && (
        <BlockAvailabilityForm
          open={creating}
          mode="create"
          initial={{ ...emptyBlock, date: boardTarget.date || todayISO() }}
          onCancel={() => setCreating(false)}
          onSubmit={handleCreate}
          showGroundPicker
          venues={venues}
          ensureGroundsForVenue={ensureGroundsForVenue}
          initialVenueId={boardTarget.venueId || forcedVenueId}
          initialGroundId={boardTarget.groundId || ""}
          forceVenueId={forcedVenueId}
          externalError={modalError}
          externalErrorDetail={modalErrorDetail}
          setExternalError={(value) => {
            setModalError(value);
            if (!value) setModalErrorDetail("");
          }}
        />
      )}

      {editing && (
        <BlockAvailabilityForm
          key={editing?._id || editing?.id}
          open={!!editing}
          mode="edit"
          initial={editing}
          onCancel={() => setEditing(null)}
          onSubmit={handleSave}
          showGroundPicker
          venues={venues}
          ensureGroundsForVenue={ensureGroundsForVenue}
          initialVenueId={editing?.venueId || forcedVenueId || ""}
          initialGroundId={editing?.groundId || ""}
          forceVenueId={forcedVenueId}
          externalError={modalError}
          externalErrorDetail={modalErrorDetail}
          setExternalError={(value) => {
            setModalError(value);
            if (!value) setModalErrorDetail("");
          }}
        />
      )}

      <ReusableModal
        open={!!exceptionRow}
        title="Open One Date"
        width="560px"
        closeOnBackdrop={false}
        onClose={() => {
          if (!exceptionBusy) setExceptionRow(null);
        }}
      >
        <div className="gavx-exception-form">
          <div className="gavx-help-text">
            This will keep the recurring contract unchanged and open only one specific
            date.
          </div>

          <div className="gavx-exception-card">
            <div>
              <b>{exceptionRow?.dateLabel || "Recurring contract"}</b>
            </div>

            {!!exceptionRow?.daysLabel && (
              <div className="gavx-exception-muted">
                Days: {exceptionRow.daysLabel}
              </div>
            )}

            <div className="gavx-exception-muted">
              Time: {exceptionRow?.startTime}–{exceptionRow?.endTime}
            </div>

            {!!exceptionRow?.note && (
              <div className="gavx-exception-muted">Note: {exceptionRow.note}</div>
            )}
          </div>

          <div className="gavx-field">
            <label>Date to open</label>

            <input
              type="date"
              className="gavx-control"
              value={exceptionDate}
              min={exceptionRow?.startDate || ""}
              max={exceptionRow?.endDate || undefined}
              onChange={(event) => setExceptionDate(event.target.value)}
            />
          </div>

          <div className="gavx-field">
            <label>Note (optional)</label>

            <input
              type="text"
              className="gavx-control"
              placeholder="Special opening for one date"
              value={exceptionNote}
              onChange={(event) => setExceptionNote(event.target.value)}
            />
          </div>

          <div className="gavx-exception-actions">
            <FormButton
              type="button"
              variant="primary"
              onClick={handleCreateException}
              disabled={exceptionBusy}
            >
              {exceptionBusy ? "Saving..." : "Save"}
            </FormButton>

            <FormButton
              type="button"
              variant="secondary"
              onClick={() => setExceptionRow(null)}
              disabled={exceptionBusy}
            >
              Cancel
            </FormButton>
          </div>
        </div>
      </ReusableModal>

      <ConfirmDialog
        open={!!deleting}
        title={
          deleting?.sourceType === "recurring"
            ? "Delete Recurring Block"
            : deleting?.sourceType === "multidate"
              ? "Delete Multi-date Block"
              : "Unblock Slot"
        }
        message={
          deleting?.sourceType === "recurring"
            ? "This action cannot be undone. The recurring block contract will be removed."
            : deleting?.sourceType === "multidate"
              ? "This action cannot be undone. All dates inside this multi-date block will be removed."
              : "This action cannot be undone. The blocked slot will be removed."
        }
        confirmText="Delete"
        cancelText="Cancel"
        confirmVariant="danger"
        loading={busyId === (deleting?._id || deleting?.id)}
        onConfirm={handleUnblock}
        onCancel={() => {
          if (busyId !== (deleting?._id || deleting?.id)) {
            setDeleting(null);
          }
        }}
      />
    </div>
  );
};

export default GroundAvailabilityPage;